<script setup lang="ts">
import { reactive, ref, computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email as emailValidator, helpers, sameAs } from '@vuelidate/validators';
import type { PhoneObject } from 'vue-tel-input';
import type { BookingProvider } from '../provider';
import { isValidVisitLanguage, normalizeVisitLanguage, type VisitLanguageValue } from '../visitLanguage';

export type FormState = 'FormFilled' | 'FormNotFilled';

const props = defineProps<{
  state: FormState;
  selectedServiceUuid: string | null;
  selectedPractitionerUuid: string | null;
  selectedDate: string | null;
  datetime: string | null;
  submitting: boolean;
  provider: BookingProvider;
  visitLanguage: VisitLanguageValue;
  visitLanguageRequired: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', payload: {
    name: string;
    lastname: string;
    phone: string;
    email: string;
    birth_date?: string;
    visit_language?: VisitLanguageValue;
  }): void;
}>();

const isMedchart = computed(() => props.provider === 'medchart');

const form = reactive({
  name: '',
  lastname: '',
  phone: '',
  email: '',
  birth_date: '',
  visit_language: normalizeVisitLanguage(props.visitLanguage, props.visitLanguageRequired),
  consentData: false,
  consentRules: false,
});

const error = ref<string | null>(null);
const isPhoneValid = ref(true);

const rules = computed(() => ({
  name: {
    required: helpers.withMessage('Imię jest wymagane.', required),
  },
  lastname: {
    required: helpers.withMessage('Nazwisko jest wymagane.', required),
  },
  phone: {
    required: helpers.withMessage('Nr telefonu jest wymagany.', required),
    isValid: helpers.withMessage('Podaj poprawny numer telefonu.', () => isPhoneValid.value),
  },
  email: {
    required: helpers.withMessage('Adres e-mail jest wymagany.', required),
    email: helpers.withMessage('Podaj poprawny adres e‑mail.', emailValidator),
  },
  birth_date: isMedchart.value
    ? { required: helpers.withMessage('Data urodzenia jest wymagana.', required) }
    : {},
  visit_language: isMedchart.value
    ? {
      valid: helpers.withMessage(
        'Wybierz język wizyty.',
        () => isValidVisitLanguage(form.visit_language, !props.visitLanguageRequired)
          && (!props.visitLanguageRequired || form.visit_language !== '')
      ),
    }
    : {},
  consentData: {
    accepted: helpers.withMessage(
      'Musisz wyrazić zgodę na przetwarzanie danych.',
      sameAs(true)
    ),
  },
  consentRules: {
    accepted: helpers.withMessage(
      'Musisz zaakceptować regulamin.',
      sameAs(true)
    ),
  },
}));

const v$ = useVuelidate(rules, form);

function setPhoneValid(phoneObject: PhoneObject) {
  isPhoneValid.value = phoneObject.valid;
}

async function handleSubmit(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  error.value = null;

  const isValid = await v$.value.$validate();
  if (!isValid) {
    error.value = 'Popraw zaznaczone pola formularza.';
    return;
  }

  emit('submit', {
    name: form.name.trim(),
    lastname: form.lastname.trim(),
    phone: form.phone.trim(),
    email: form.email.trim(),
    ...(isMedchart.value ? { birth_date: form.birth_date } : {}),
    ...(isMedchart.value ? { visit_language: form.visit_language } : {}),
  });
}

const termsUrl = ref(window?.MedfileBookingWidgetConfig?.additional?.termsUrl ?? '');
const privacyPolicyUrl = ref(window?.MedfileBookingWidgetConfig?.additional?.privacyPolicyUrl ?? '');
</script>

<template>
  <form class="mbw-form">

    <input type="hidden" :value="selectedServiceUuid" name="selected_service_uuid" />
    <input type="hidden" :value="selectedPractitionerUuid" name="selected_practitioner_uuid" />
    <input type="hidden" :value="selectedDate" name="selected_date" />

    <div class="mbw-form-field" :class="{ 'mbw-form-field--error': v$.name.$error }">
      <label for="patient_name">Imię</label>
      <input
        id="patient_name"
        v-model="form.name"
        type="text"
        name="patient_name"
        required
        @blur="v$.name.$touch()"
        :aria-invalid="v$.name.$error || undefined"
      />
      <div v-if="v$.name.$error" class="mbw-form-field-error">
        {{ v$.name.$errors[0].$message }}
      </div>
    </div>

    <div class="mbw-form-field" :class="{ 'mbw-form-field--error': v$.lastname.$error }">
      <label for="patient_lastname">Nazwisko</label>
      <input
        id="patient_lastname"
        v-model="form.lastname"
        type="text"
        name="patient_lastname"
        required
        @blur="v$.lastname.$touch()"
        :aria-invalid="v$.lastname.$error || undefined"
      />
      <div v-if="v$.lastname.$error" class="mbw-form-field-error">
        {{ v$.lastname.$errors[0].$message }}
      </div>
    </div>

    <div v-if="isMedchart" class="mbw-form-field mbw-form-colspan-2" :class="{ 'mbw-form-field--error': v$.birth_date.$error }">
      <label for="birth_date">Data urodzenia</label>
      <input
        id="birth_date"
        v-model="form.birth_date"
        type="date"
        name="birth_date"
        required
        @blur="v$.birth_date.$touch()"
        :aria-invalid="v$.birth_date.$error || undefined"
      />
      <div v-if="v$.birth_date.$error" class="mbw-form-field-error">
        {{ v$.birth_date.$errors[0].$message }}
      </div>
    </div>

    <div v-if="isMedchart" class="mbw-form-field mbw-form-colspan-2" :class="{ 'mbw-form-field--error': v$.visit_language.$error }">
      <label for="visit_language">Język wizyty</label>
      <select
        id="visit_language"
        v-model="form.visit_language"
        name="visit_language"
        :required="visitLanguageRequired"
        @blur="v$.visit_language.$touch()"
        :aria-invalid="v$.visit_language.$error || undefined"
      >
        <option v-if="!visitLanguageRequired" value="">Wybierz język wizyty</option>
        <option value="pl-PL">Polski</option>
        <option value="en-GB">Angielski</option>
        <option value="uk-UA">Ukraiński</option>
      </select>
      <div v-if="v$.visit_language.$error" class="mbw-form-field-error">
        {{ v$.visit_language.$errors[0].$message }}
      </div>
    </div>

    <div class="mbw-form-field" :class="{ 'mbw-form-field--error': v$.phone.$error }">
      <label for="phone">Nr telefonu</label>
      <vue-tel-input
        id="phone"
        v-model="form.phone"
        mode="international"
        :input-options="{ placeholder: '' }"
        :preferred-countries="['pl', 'ua']"
        @validate="setPhoneValid"
        @blur="v$.phone.$touch()"
      />
      <div v-if="v$.phone.$error" class="mbw-form-field-error">
        {{ v$.phone.$errors[0].$message }}
      </div>
    </div>

    <div class="mbw-form-field" :class="{ 'mbw-form-field--error': v$.email.$error }">
      <label for="email">Adres e-mail</label>
      <input
        id="email"
        v-model="form.email"
        type="email"
        name="email"
        required
        @blur="v$.email.$touch()"
        :aria-invalid="v$.email.$error || undefined"
      />
      <div v-if="v$.email.$error" class="mbw-form-field-error">
        {{ v$.email.$errors[0].$message }}
      </div>
    </div>

    <div
      class="mbw-form-field mbw-form-field--checkbox mbw-form-colspan-2"
      :class="{ 'mbw-form-field--error': v$.consentData.$error }"
    >
      <label>
        <input
          v-model="form.consentData"
          type="checkbox"
          name="consent_data"
          required
          @change="v$.consentData.$touch()"
          :aria-invalid="v$.consentData.$error || undefined"
        />
        <span>
          Wyrażam zgodę na przetwarzanie moich danych osobowych przez Centrum Rehabilitacji Koala
          i akceptuję
          <a :href="privacyPolicyUrl" class="mbw-form-link" target="_blank" rel="noopener noreferrer">
            Politykę prywatności.
          </a>
        </span>
      </label>
    </div>

    <div v-if="v$.consentData.$error" class="mbw-form-field-error mbw-form-colspan-2">
      {{ v$.consentData.$errors[0].$message }}
    </div>

    <div
      class="mbw-form-field mbw-form-field--checkbox mbw-form-colspan-2"
      :class="{ 'mbw-form-field--error': v$.consentRules.$error }"
    >
      <label>
        <input
          v-model="form.consentRules"
          type="checkbox"
          name="consent_rules"
          required
          @change="v$.consentRules.$touch()"
          :aria-invalid="v$.consentRules.$error || undefined"
        />
        <span>
          Akceptuję postanowienia
          <a :href="termsUrl" class="mbw-form-link" target="_blank" rel="noopener noreferrer">
            Regulaminu.
          </a>
        </span>
      </label>
    </div>
    
    <div v-if="v$.consentRules.$error" class="mbw-form-field-error mbw-form-colspan-2">
      {{ v$.consentRules.$errors[0].$message }}
    </div>

    <div v-if="error" class="mbw-form-error mbw-form-colspan-2">
      {{ error }}
    </div>

    <button type="submit" class="mbw-form-submit mbw-form-colspan-2" :disabled="submitting" @click="handleSubmit">
      {{ submitting ? 'Wysyłanie...' : 'Zarezerwuj termin' }}
    </button>
  </form>
</template>
